import { Link } from '@/lib/router';
import { useAuth } from '@/contexts/AuthContext';
import {
  PublicPageFooter,
  PublicPageHeader,
  SkipToMainContent,
} from '@/components/public/PublicPageChrome';
import {
  CLAUDE_INTEGRATIONS_URL,
  CONNECTOR_URL,
} from '@/lib/connector';
import { PRO_PRICE_LABEL } from '@/lib/pricing';

export function LandingPage() {
  const { user, loading } = useAuth();

  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-surface-0 font-prose text-text-primary">
      <SkipToMainContent />
      <PublicPageHeader active="home" />

      <main
        id="main-content"
        tabIndex={-1}
        className="min-w-0 flex-1 outline-none"
      >
        <Hero authenticated={Boolean(user)} sessionLoading={loading} />
        <WhyDifferent />
        <Compare />
        <TryIt />
        <Tiers authenticated={Boolean(user)} sessionLoading={loading} />
        <Principles />
        <Manifesto />
      </main>

      <PublicPageFooter active="home" />
    </div>
  );
}

function Hero({
  authenticated,
  sessionLoading,
}: {
  authenticated: boolean;
  sessionLoading: boolean;
}) {
  return (
    <section
      aria-labelledby="landing-title"
      className="mx-auto max-w-[960px] min-w-0 px-6 pb-12 pt-16 max-mobile:px-4 max-mobile:pb-8 max-mobile:pt-10"
    >
      <p
        className="m-0 font-ui text-xs font-semibold uppercase tracking-[0.08em] text-navy-500"
      >
        Prometheus
      </p>
      <h1
        id="landing-title"
        className="mt-3 min-w-0 font-prose text-2xl font-semibold leading-tight text-text-primary [overflow-wrap:anywhere] max-mobile:text-xl"
      >
        Free primary-law research for{' '}
        <span className="text-navy-500">Claude.</span>
      </h1>
      <p className="mt-5 text-text-secondary font-prose text-md leading-relaxed max-w-[680px]">
        Prometheus is a structured legal authority layer that lets Claude
        search cases, statutes, regulations, rules, and citation
        relationships — then answer with verifiable citations to primary law.
      </p>
      <p className="mt-3 font-prose text-md text-text-primary max-w-[680px]">
        <strong>
          Free for public-law research. Pro for patent intelligence and
          practitioner workflows.
        </strong>
      </p>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        {sessionLoading ? (
          <span className="inline-flex min-h-10 items-center rounded-md bg-surface-2 px-5 font-ui text-sm text-text-secondary">
            Checking account…
          </span>
        ) : (
          <Link
            to={authenticated ? '/account' : '/auth?mode=signup'}
            className="inline-flex min-h-10 items-center rounded-md bg-text-primary px-5 font-ui text-sm font-medium text-surface-0 no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
          >
            {authenticated ? 'Open account' : 'Create account'}
          </Link>
        )}
        <a
          href={CLAUDE_INTEGRATIONS_URL}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Connect in Claude (opens in a new tab)"
          className="inline-flex min-h-10 items-center rounded-md border border-border px-5 font-ui text-sm font-medium text-text-secondary no-underline hover:bg-surface-1 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
        >
          Connect in Claude
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
        <Link
          to="/docs"
          className="inline-flex min-h-10 items-center rounded-md border border-border px-5 font-ui text-sm font-medium text-text-secondary no-underline hover:bg-surface-1 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
        >
          Try these prompts
        </Link>
      </div>

      <pre className="mt-7 max-w-full overflow-x-auto rounded-md border border-border bg-surface-2 px-4 py-3 font-mono text-sm text-text-primary">
        <code>{CONNECTOR_URL}</code>
      </pre>
    </section>
  );
}

const WHY_DIFFERENT = [
  {
    title: 'Not raw documents.',
    body: 'Prometheus breaks primary law into retrieval-tuned passages, so Claude can find the rule, holding, test, or statutory subsection that actually matters — not just a PDF.',
  },
  {
    title: 'Not model memory.',
    body: 'Claude reasons over retrieved authority instead of guessing from training data. Every answer ties to a source Claude pulled live.',
  },
  {
    title: 'Not just keyword search.',
    body: 'Verify citations, resolve statutes and rules, walk the citation graph, and retrieve the specific passage from the underlying source.',
  },
  {
    title: 'Free at the point of use.',
    body: 'You bring Claude. Prometheus provides the legal authority layer.',
  },
];

function WhyDifferent() {
  return (
    <section className="mx-auto max-w-[960px] min-w-0 border-t border-border-subtle px-6 py-12 max-mobile:px-4">
      <SectionLabel>Why it is different</SectionLabel>
      <h2 className="mt-2 min-w-0 font-prose text-lg font-semibold text-text-primary [overflow-wrap:anywhere]">
        A structured legal authority layer — not a document search box.
      </h2>
      <div className="mt-6 grid grid-cols-2 gap-5 max-tablet:grid-cols-1">
        {WHY_DIFFERENT.map((p) => (
          <article
            key={p.title}
            className="min-w-0 rounded-lg border border-border bg-surface-white p-5"
          >
            <h3 className="m-0 min-w-0 font-prose text-base font-semibold text-text-primary [overflow-wrap:anywhere]">
              {p.title}
            </h3>
            <p className="m-0 mt-2 min-w-0 font-ui text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]">
              {p.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

const COMPARISON: Array<{ others: string; prometheus: React.ReactNode }> = [
  {
    others: 'Return raw opinion text from a single source',
    prometheus:
      'Returns structured legal authority across cases, statutes, regulations, rules, and the citation graph',
  },
  {
    others: 'Search-style results that you (or Claude) sort through',
    prometheus:
      'Retrieval-tuned passages, holdings, and statutory subsections — the actual rule or test that matters',
  },
  {
    others: 'Useful for finding cases',
    prometheus: 'Useful for answering legal questions with sources',
  },
  {
    others: 'Verify a citation? You check manually.',
    prometheus: (
      <>
        Built-in <code className="font-mono text-xs">verify_citation</code>{' '}
        tool catches hallucinated cites
      </>
    ),
  },
  {
    others: 'Structured retrieval is the paid/enterprise tier',
    prometheus:
      'Structured retrieval at the free tier; Pro adds patent + practitioner workflows',
  },
];

function Compare() {
  return (
    <section className="mx-auto max-w-[960px] min-w-0 border-t border-border-subtle px-6 py-12 max-mobile:px-4">
      <SectionLabel>Compared to other legal MCPs</SectionLabel>
      <h2 className="mt-2 min-w-0 font-prose text-lg font-semibold text-text-primary [overflow-wrap:anywhere]">
        Structured retrieval has been the paid tier. Not anymore.
      </h2>

      <div className="mt-6 min-w-0 overflow-hidden rounded-lg border border-border bg-surface-white">
        <div className="grid grid-cols-2 bg-surface-1 border-b border-border max-mobile:hidden">
          <div className="px-4 py-3 font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary">
            Other legal MCPs
          </div>
          <div
            className="border-l border-border px-4 py-3 font-ui text-xs font-semibold uppercase tracking-[0.06em] text-navy-500"
          >
            Prometheus
          </div>
        </div>

        {COMPARISON.map((row, i) => (
          <div
            key={i}
            className="grid min-w-0 grid-cols-2 border-t border-border-subtle first:border-t-0 max-mobile:grid-cols-1"
          >
            <div className="min-w-0 px-4 py-4 font-ui text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]">
              <span className="hidden max-mobile:block font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary mb-1">
                Other legal MCPs
              </span>
              {row.others}
            </div>
            <div className="min-w-0 border-l border-border-subtle px-4 py-4 font-ui text-sm leading-snug text-text-primary [overflow-wrap:anywhere] max-mobile:border-l-0 max-mobile:border-t max-mobile:bg-surface-1">
              <span
                className="mb-1 hidden font-ui text-xs font-semibold uppercase tracking-[0.06em] text-navy-500 max-mobile:block"
              >
                Prometheus
              </span>
              {row.prometheus}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const TRY_IT = [
  {
    category: 'Citation verification',
    prompt:
      'Is 517 U.S. 370 actually Markman v. Westview Instruments? What was the holding?',
  },
  {
    category: 'Doctrine research',
    prompt:
      'What did SCOTUS hold in Mathews v. Eldridge about due process balancing? Give me the three-factor test verbatim.',
  },
  {
    category: 'Practitioner-grade messy question',
    prompt:
      "A plaintiff brings a § 1983 claim against a city based on a police officer's conduct. What does the plaintiff have to show to establish municipal liability, and what cases control?",
  },
];

function TryIt() {
  return (
    <section className="mx-auto max-w-[960px] min-w-0 border-t border-border-subtle px-6 py-12 max-mobile:px-4">
      <SectionLabel>Try these in Claude</SectionLabel>
      <h2 className="mt-2 min-w-0 font-prose text-lg font-semibold text-text-primary [overflow-wrap:anywhere]">
        Paste any of these into a Claude chat with Prometheus connected.
      </h2>
      <div className="mt-6 grid grid-cols-3 gap-4 max-tablet:grid-cols-1">
        {TRY_IT.map((ex) => (
          <article
            key={ex.category}
            className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-surface-white p-5"
          >
            <p
              className="m-0 font-ui text-xs font-semibold uppercase tracking-[0.06em] text-navy-500"
            >
              {ex.category}
            </p>
            <p
              className="m-0 min-w-0 rounded-sm border-l-2 border-l-navy-500 bg-surface-1 py-2 pl-3 font-mono text-xs leading-snug text-text-primary [overflow-wrap:anywhere]"
            >
              {ex.prompt}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

const FREE_TIER = [
  'Federal + state caselaw search',
  'U.S. Code, U.S. Constitution, Federal Rules of Civil/Criminal/Appellate/Bankruptcy Procedure',
  'Code of Federal Regulations',
  'All 50 state statute codes',
  'Citation verification + case lookup',
];

const FREE_NOTE =
  'Built for pro se litigants, journalists, law students, public-interest attorneys, and any lawyer who needs accurate primary law without a Lexis/Westlaw subscription.';

const PRO_TIER = [
  'Everything in Free',
  'Federal agency guidance',
  'Manual of Patent Examining Procedure (MPEP)',
  'Patent vertical: claims, OA rejection patterns, examiner data',
  '3,500+ structured CAFC holdings (claim construction, obviousness, written description, enablement, §101, prosecution disclaimer)',
  'EDGAR + tax + SEC guidance',
];

const PRO_NOTE =
  'For attorneys building briefs, doing prior-art search, or working active matters who need the depth of a paid research platform inside Claude.';

function Tiers({
  authenticated,
  sessionLoading,
}: {
  authenticated: boolean;
  sessionLoading: boolean;
}) {
  return (
    <section className="mx-auto max-w-[960px] min-w-0 border-t border-border-subtle px-6 py-12 max-mobile:px-4">
      <SectionLabel>Free and Pro</SectionLabel>
      <h2 className="mt-2 min-w-0 font-prose text-lg font-semibold text-text-primary [overflow-wrap:anywhere]">
        The public-record corpus is free. Practitioner workflows are Pro.
      </h2>

      <div className="mt-6 grid grid-cols-2 gap-4 max-tablet:grid-cols-1">
        <article className="flex min-w-0 flex-col rounded-lg border border-border bg-surface-white p-6 max-mobile:p-5">
          <h3 className="font-prose text-base font-semibold text-text-primary m-0">
            Free
          </h3>
          <p className="font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary mt-1">
            Public-record access · no account required
          </p>
          <ul className="mt-4 flex flex-col gap-2 font-ui text-sm text-text-secondary list-none p-0">
            {FREE_TIER.map((line) => (
              <li key={line} className="flex min-w-0 gap-2 [overflow-wrap:anywhere]">
                <span className="text-text-tertiary" aria-hidden="true">
                  •
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 font-ui text-xs text-text-tertiary leading-snug m-0 pt-4 border-t border-border-subtle">
            {FREE_NOTE}
          </p>
        </article>

        <article
          className="flex min-w-0 flex-col rounded-lg border-2 border-navy-500 bg-surface-white p-6 max-mobile:p-5"
        >
          <h3 className="font-prose text-base font-semibold text-text-primary m-0">
            Pro
          </h3>
          <p
            className="mt-1 font-ui text-xs uppercase tracking-[0.06em] text-navy-500"
          >
            Patent intelligence + practitioner workflows
          </p>
          <ul className="mt-4 flex flex-col gap-2 font-ui text-sm text-text-secondary list-none p-0">
            {PRO_TIER.map((line) => (
              <li key={line} className="flex min-w-0 gap-2 [overflow-wrap:anywhere]">
                <span className="text-navy-500" aria-hidden="true">
                  •
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 font-ui text-xs text-text-tertiary leading-snug m-0 pt-4 border-t border-border-subtle">
            {PRO_NOTE}
          </p>
          {sessionLoading ? (
            <span className="mt-5 inline-flex min-h-10 items-center justify-center rounded-md bg-surface-2 px-5 font-ui text-sm text-text-secondary">
              Checking account…
            </span>
          ) : (
            <Link
              to={authenticated ? '/account' : '/auth?mode=signup&plan=pro'}
              className="mt-5 inline-flex min-h-10 items-center justify-center rounded-md bg-text-primary px-5 font-ui text-sm font-medium text-surface-0 no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
            >
              {authenticated ? 'Review Pro options' : 'Start Pro'} —{' '}
              {PRO_PRICE_LABEL}
            </Link>
          )}
        </article>
      </div>
    </section>
  );
}

const PRINCIPLES = [
  {
    title: 'Primary sources only.',
    body: 'Prometheus retrieves from the underlying statutes, regulations, and judicial opinions — not summaries, headnotes, or secondary commentary.',
  },
  {
    title: 'Source-grade citations.',
    body: "Every answer links to the canonical citation — reporter cite, statute section, rule number, paragraph — so you can verify the work without leaving Claude. The source URL goes to a public-record page (CourtListener, GovInfo, USPTO, EDGAR). Catch a fabricated citation? Email us — we'll fix it.",
  },
  {
    title: 'No training on your queries.',
    body: 'Your queries are embedded transiently and discarded. We do not retain query text, build user-specific models, or share queries with third parties.',
  },
  {
    title: 'Public-domain data.',
    body: 'The corpus is built from public-record sources: CourtListener, GovInfo, USPTO, EDGAR. State law is uncopyrightable per Veeck v. Southern Building Code Congress. Federal law is public domain.',
  },
];

function Principles() {
  return (
    <section
      aria-labelledby="principles-title"
      className="mx-auto max-w-[960px] min-w-0 border-t border-border-subtle px-6 py-12 max-mobile:px-4"
    >
      <SectionLabel>Principles</SectionLabel>
      <h2
        id="principles-title"
        className="mt-2 min-w-0 font-prose text-lg font-semibold text-text-primary [overflow-wrap:anywhere]"
      >
        Research built on verifiable authority.
      </h2>
      <div className="mt-6 grid grid-cols-2 gap-5 max-tablet:grid-cols-1">
        {PRINCIPLES.map((p) => (
          <article key={p.title} className="min-w-0">
            <h3 className="m-0 min-w-0 font-prose text-base font-semibold text-text-primary [overflow-wrap:anywhere]">
              {p.title}
            </h3>
            <p className="m-0 mt-2 min-w-0 font-ui text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]">
              {p.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section
      aria-labelledby="manifesto-title"
      className="mx-auto max-w-[960px] min-w-0 border-t border-border-subtle px-6 py-12 max-mobile:px-4"
    >
      <h2 id="manifesto-title" className="sr-only">
        Why Prometheus exists
      </h2>
      <div
        className="min-w-0 rounded-lg border-l-4 border-l-navy-500 bg-surface-1 px-7 py-6 max-mobile:px-5"
      >
        <p className="m-0 min-w-0 font-prose text-md leading-relaxed text-text-primary [overflow-wrap:anywhere]">
          Legal research inside AI agents should be free at the point of use.
          The agent (Claude) already costs the user something. The substrate
          shouldn't need a second tollbooth on top of that.
        </p>
        <p className="m-0 mt-4 min-w-0 font-prose text-md leading-relaxed text-text-primary [overflow-wrap:anywhere]">
          Lawyers should pay for provenance, work product, privacy, and
          professional controls — not basic access to primary law.
        </p>
      </div>
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="m-0 font-ui text-xs font-semibold uppercase tracking-[0.08em] text-navy-500"
    >
      {children}
    </p>
  );
}
